import { CELL_STATE } from "../cell";
import { IS_SAFARI, IS_WEB } from "../config";
import { XLINKS, XMLNS } from "../constants";
import {
  gameEl,
  getBodyEls,
  getCatEls,
  getHeadEls,
  stepsEl,
} from "../elements";
import "../ripple";
import { playMp3 } from "../sound";
import store from "../store";
import { ADD_X_0, ADD_X_1, ADD_Y_0, cellsBlocked } from "./cells";
import { GAME_MODE, gameState, levelDef } from "./gameState";
import { PromiseExecutor, sleep } from "../promise";

export const DIRECTION = {
  RIGHT: 0,
  BOTTOM_RIGHT: 1,
  BOTTOM_LEFT: 2,
  LEFT: 3,
  TOP_LEFT: 4,
  TOP_RIGHT: 5,
};

class JumpAnimationPromiseExecutor extends PromiseExecutor {
  #animStart;
  #animFrame;
  #animDurationPerFrame;
  #resolve;
  #cat;

  _promiseCallback = (resolve) => {
    const [cat] = this._args;
    this.#cat = cat;
    this.#animStart = null;
    this.#animFrame = 1;
    this.#animDurationPerFrame = 64 / store.animationSpeed;
    this.#resolve = resolve;
    requestAnimationFrame(this.#animate);
  };

  #animate = (timestamp) => {
    if (this.#animStart === null) this.#animStart = timestamp;

    const elapsedTime = timestamp - this.#animStart;
    const currentFrame = Math.floor(elapsedTime / this.#animDurationPerFrame);

    if (currentFrame !== this.#animFrame) {
      this.#animFrame = currentFrame;
      this.#cat.display(
        this.#cat.px,
        this.#cat.py,
        this.#cat.dir,
        this.#animFrame % this.#cat.numFrames
      );
    }

    if (elapsedTime < this.#animDurationPerFrame * this.#cat.numFrames)
      requestAnimationFrame(this.#animate);
    else {
      this.#cat.px += 34 * this.#cat.dirX[this.#cat.dir];
      this.#cat.py += 26 * this.#cat.dirY[this.#cat.dir];
      this.#cat.display(this.#cat.px, this.#cat.py, this.#cat.dir);
      this.#resolve();
    }
  };
}

export class Cat {
  #jumping;
  #jumpAnimationPromiseExecutor;

  constructor() {
    this.x = 0;
    this.y = 0;
    this.px = 0;
    this.py = 0;
    this.dir = 0;
    this.dirX = [1, 0.5, -0.5, -1, -0.5, 0.5];
    this.dirY = [0, 1, 1, 0, -1, -1];
    this.won = false;
    this.lx = [];
    this.ly = [];
    this.ld = [];
    this.lx2 = [];
    this.ly2 = [];
    this.frozen = false;
    this.numFrames = 5;
    this.#jumping = false;

    // this._animStart = null;
    // this._animFrame = 1;
    // this._animDurationPerFrame = 64;

    this.#jumpAnimationPromiseExecutor = new JumpAnimationPromiseExecutor();
  }

  assign(otherCat) {
    this.x = otherCat.x;
    this.y = otherCat.y;
    this.px = otherCat.px;
    this.py = otherCat.py;
    this.dir = otherCat.dir;
    this.dirX = otherCat.dirX;
    this.dirY = otherCat.dirY;
    this.won = otherCat.won;
    this.lx = otherCat.lx;
    this.ly = otherCat.ly;
    this.ld = otherCat.ld;
    this.lx2 = otherCat.lx2;
    this.ly2 = otherCat.ly2;
    this.frozen = otherCat.frozen;
    this.numFrames = otherCat.numFrames;
    this.#jumping = otherCat.#jumping;
  }

  freeze() {
    this.frozen = true;
    gameState.cells[this.y][this.x].use.setAttributeNS(null, "fill", "#79FFFC");
    Array.prototype.forEach.call(getCatEls(), (element) => {
      element.style.fill = "#023240";
    });
    Array.prototype.forEach.call(getBodyEls(), (element) => {
      element.style.stroke = "#023240";
    });
    Array.prototype.forEach.call(getHeadEls(), (element) => {
      element.style.stroke = "#023240";
      element.style.fill = "#023240";
    });
  }

  init(x, y) {
    this.shape = document.createElementNS(XMLNS, "use");
    this.shape.setAttributeNS(null, "class", "cat");
    this.setLocation(x, y);
    this.px = (y % 2 == 0 ? 5 : 20) + 34 * this.x;
    this.py = -15 + 26 * this.y;
    if (gameState.cells[this.y][this.x].state != CELL_STATE.NON_BLOCKABLE)
      gameState.cells[this.y][this.x].state = CELL_STATE.FREE;
    gameState.cells[this.y][this.x].id &&
      gameState.cells[this.y][this.x].id.setAttributeNS(
        XLINKS,
        "xlink:href",
        "#free-cell"
      );
    // this.demo(this.px, this.py);
  }

  async demo(px, py) { // eslint-disable-line no-unused-vars
    throw new Error("Not implemented");
  }

  setLocation(x, y) {
    this.x = x;
    this.y = y;
  }

  display(px, py, dir = null, frame = 0) {
    this.shape.setAttributeNS(null, "transform", `translate(${px},${py})`);
    if (dir === null) this.shape.removeAttributeNS(XLINKS, "xlink:href");
    else {
      const href = `#f${dir}${frame}`;
      this.shape.setAttributeNS(XLINKS, "xlink:href", href);
    }
  }

  async jump() {
    if (this.#jumping) throw new Error("🐈‍ Already jumping");
    this.#jumping = true;
    if (!(IS_WEB && IS_SAFARI)) playMp3("swoosh");
    await this.#jumpAnimationPromiseExecutor.run(this);
    this.#jumping = false;
  }

  isJumping = () => this.#jumping;

  run() {
    const dir = this.dir;
    return new Promise((resolve) => {
      let t = 0;
      let end = 0;
      let frame = 1;
      for (let i = 1; i < 20; i++) {
        t++;
        setTimeout(() => {
          // TODO: make this non-anonymous
          frame++;
          if (frame == 0) frame++;
          if (frame == 5) frame = 2;
          this.px += 0.4 * 34 * this.dirX[dir];
          this.py += 0.4 * 26 * this.dirY[dir];
          this.display(this.px, this.py, dir, frame);
          if (end++ == 18) resolve();
        }, (t * 64) / store.animationSpeed);
      }
    });
  }

  goOut() {
    return this._goOut((x, y, dir) => {
      this.setLocation(x, y);
      this.dir = dir;
    });
  }

  _goOut(cb = () => {}) {
    for (let i = 0; i < 6; ++i) {
      const x = this.y % 2 ? this.x + ADD_X_1[i] : this.x + ADD_X_0[i];
      const y = this.y + ADD_Y_0[i];
      const cellState = gameState.cells[y][x].state;
      if (
        cellState == CELL_STATE.OUTSIDE ||
        cellState == CELL_STATE.NON_BLOCKABLE_OUTSIDE
      ) {
        cb(x, y, i);
        return true;
      }
    }
    return false;
  }

  gotoWin(excludeList) {
    return this._gotoWin(excludeList, (x, y, dir, won) => {
      this.setLocation(x, y);
      this.dir = dir;
      this.won = won;
    });
  }

  _gotoWin(excludeList, cb = () => {}) {
    for (let i = 0; i < 6; ++i) {
      const x = this.y % 2 ? this.x + ADD_X_1[i] : this.x + ADD_X_0[i];
      const y = this.y + ADD_Y_0[i];
      if (this.isBlocked(y, x, excludeList)) continue;
      if (gameState.cells[y][x].win) {
        cb(x, y, i, true);
        return true;
      }
    }
    return false;
  }

  hasLostQuick() {
    return !canCatEscape(gameState.cells, this);
  }

  hasWon(excludeList) {
    return this._goOut() || this._gotoWin(excludeList);
  }

  getCoordNum() {
    return this.y * 100 + this.x;
  }

  getNearest(excludeList) {
    gameState.cells[this.y][this.x].po = 0;
    this.lx[0] = this.x;
    this.ly[0] = this.y;
    let m = 1;
    let n = 999;
    let p;
    let x;
    let y;
    let kx;
    let ky;
    for (let po = 1; po < 200; po++) {
      p = 0;
      for (let i = 0; i < m; ++i) {
        x = this.lx[i];
        y = this.ly[i];
        for (let k = 0; k < 6; ++k) {
          kx = y % 2 ? x + ADD_X_1[k] : x + ADD_X_0[k];
          ky = y + ADD_Y_0[k];
          if (this.isBlocked(ky, kx, excludeList)) continue;
          if (gameState.cells[ky][kx].po >= 0) continue;
          gameState.cells[ky][kx].po = po;
          this.lx2[p] = kx;
          this.ly2[p] = ky;
          p++;
          if (gameState.cells[ky][kx].win && po < n) n = po;
        }
      }
      if (p == 0) break;
      for (let i = 0; i < p; ++i) {
        this.lx[i] = this.lx2[i];
        this.ly[i] = this.ly2[i];
      }
      m = p;
    }
    if (n == 999) return false;
    p = 0;
    for (let i = 0; i < levelDef.fieldSize; ++i) {
      for (let j = 0; j < levelDef.fieldSize; ++j) {
        if (gameState.cells[i][j].po == n && gameState.cells[i][j].win) {
          this.lx[p] = j;
          this.ly[p] = i;
          p++;
        }
      }
    }
    if (p == 0) return false;
    let d = Math.floor(Math.random() * p);
    x = this.lx[d];
    y = this.ly[d];
    for (let r = 0; r < 200; ++r) {
      p = 0;
      for (let k = 0; k < 6; ++k) {
        kx = y % 2 ? x + ADD_X_1[k] : x + ADD_X_0[k];
        ky = y + ADD_Y_0[k];
        if (
          this.isBlocked(ky, kx, excludeList) ||
          gameState.cells[ky][kx].po >= gameState.cells[y][x].po
        )
          continue;
        this.lx[p] = kx;
        this.ly[p] = ky;
        this.ld[p] = k;
        p++;
      }
      if (p == 0) return false;
      d = Math.floor(Math.random() * p);
      x = this.lx[d];
      y = this.ly[d];
      if (gameState.cells[y][x].po == 1) {
        this.setLocation(x, y);
        this.dir = 0;
        for (let k = 0; k < 6; ++k) {
          kx = y % 2 ? x + ADD_X_1[k] : x + ADD_X_0[k];
          ky = y + ADD_Y_0[k];
          if (gameState.cells[ky][kx].po == 0) this.dir = (k + 3) % 6;
        }
        return true;
      }
    }
    return false;
  }

  randMove(excludeList) {
    const x = this.x;
    const y = this.y;
    let p = 0;
    for (let k = 0; k < 6; ++k) {
      const kx = y % 2 ? x + ADD_X_1[k] : x + ADD_X_0[k];
      const ky = y + ADD_Y_0[k];
      if (this.isBlocked(ky, kx, excludeList)) continue;
      this.lx[p] = kx;
      this.ly[p] = ky;
      this.ld[p] = k;
      p++;
    }
    if (p == 0) return false;
    const d = Math.floor(Math.random() * p);
    this.setLocation(this.lx[d], this.ly[d]);
    this.dir = this.ld[d];
    return true;
  }

  play(excludeList) {
    let promise = Promise.resolve();
    if (this.frozen) {
      Array.prototype.forEach.call(getCatEls(), (element) => {
        element.style.fill = store.themes[4];
      });
      Array.prototype.forEach.call(getBodyEls(), (element) => {
        element.style.stroke = store.themes[4];
      });
      Array.prototype.forEach.call(getHeadEls(), (element) => {
        element.style.stroke = store.themes[4];
        element.style.fill = store.themes[4];
      });
      this.frozen = false;
      return { promise, catState: { lost: this.hasLostQuick(), won: false } };
    }
    for (let i = 0; i < levelDef.fieldSize; i++)
      for (let j = 0; j < levelDef.fieldSize; j++)
        gameState.cells[i][j].po = -1;
    const goOut = this.goOut();
    const won = goOut || this.gotoWin(excludeList);
    let lost = !won && !this.getNearest(excludeList);
    let jump = true;
    if (lost) jump = this.randMove(excludeList);
    if (jump) promise = this.jump();
    const trapped = lost;
    if (gameState.mode == GAME_MODE.CLASSIC) lost = lost && !jump;
    return {
      promise,
      catState: { lost, won, trapped },
    };
  }

  isBlocked(i, j, excludeList = []) {
    gameState;
    return excludeList.includes(i * 100 + j) || cellsBlocked(i, j);
  }

  copy() {
    const copy = new Cat();
    copy.x = this.x;
    copy.y = this.y;
    copy.px = this.px;
    copy.py = this.py;
    copy.dir = this.dir;
    copy.dirX = this.dirX;
    copy.dirY = this.dirY;
    copy.won = this.won;
    copy.lx = this.lx;
    copy.ly = this.ly;
    copy.ld = this.ld;
    copy.lx2 = this.lx2;
    copy.ly2 = this.ly2;
    copy.frozen = this.frozen;
    copy.shape = this.shape;
    copy.#jumping = this.#jumping;
    copy._animStart = this._animStart;
    copy._animFrame = this._animFrame;
    copy._animDurationPerFrame = this._animDurationPerFrame;
    return copy;
  }
}

let gSpriteCounter = 0;

function createSprite(numSheets = 1) {
  const fragment = document.createDocumentFragment();
  const sprite = document.createElementNS(XMLNS, "g");
  sprite.setAttributeNS(
    null,
    "clip-path",
    `url(#clip-sprite-${gSpriteCounter})`
  );
  const spriteId = `sprite-${gSpriteCounter}`;
  sprite.setAttributeNS(null, "id", spriteId);
  sprite.classList.add("sprite");
  const spriteSheets = document.createElementNS(XMLNS, "g");
  const clipPath = document.createElementNS(XMLNS, "clipPath");
  clipPath.setAttributeNS(null, "id", `clip-sprite-${gSpriteCounter}`);
  const rect = document.createElementNS(XMLNS, "rect");
  clipPath.appendChild(rect);
  for (let i = 0; i < numSheets; i++) {
    const image = document.createElementNS(XMLNS, "image");
    spriteSheets.appendChild(image);
  }
  sprite.appendChild(spriteSheets);
  sprite.appendChild(clipPath);
  fragment.appendChild(sprite);
  gameEl.querySelector("defs").appendChild(fragment);
  gSpriteCounter++;
  return [spriteId, spriteSheets.children, rect];
}

export function resetSprites() {
  const sprites = gameEl.querySelectorAll(".sprite");
  for (const sprite of sprites) {
    sprite.remove();
  }
  gSpriteCounter = 0;
}

class SpriteCat extends Cat {
  constructor({
    scale = 1,
    spriteWidth,
    spriteHeight,
    width,
    height,
    frameOffset = 0,
    numFrames = 6,
    numCols = 6,
    numSheets = 1,
  } = {}) {
    super();
    this.scale = scale;
    this.spriteWidth = spriteWidth;
    this.spriteHeight = spriteHeight;
    this.width = width;
    this.height = height;
    this.frameOffset = frameOffset;
    this.numFrames = numFrames;
    this.numCols = numCols;
    const [spriteId, spriteSheets, rect] = createSprite(numSheets);
    this.scale = scale;
    this.spriteSheets = spriteSheets;
    this.spriteId = spriteId;
    this.#setDims(rect, spriteSheets);
  }

  #setDims(rect, spriteSheets) {
    rect.setAttributeNS(null, "width", this.spriteWidth * this.scale);
    rect.setAttributeNS(null, "height", this.spriteHeight * this.scale);
    for (const spriteSheet of spriteSheets) {
      spriteSheet.setAttributeNS(null, "width", this.width * this.scale);
      spriteSheet.setAttributeNS(null, "height", this.height * this.scale);
      spriteSheet.style.imageRendering = "pixelated";
    }
  }

  async demo(px, py) {
    for (const dir in DIRECTION) {
      for (let frame = 0; frame < this.numFrames; frame++) {
        this.display(px, py, DIRECTION[dir], frame);
        await sleep(150);
      }
    }
    this.demo(px, py);
  }

  setSpriteSheetLocation(x, y, flipX, frameWidth) {
    for (const spriteSheet of this.spriteSheets) {
      spriteSheet.setAttribute("x", -x);
      spriteSheet.setAttribute("y", -y);
      if (flipX)
        spriteSheet.setAttributeNS(
          null,
          "transform",
          `translate(${frameWidth},0) scale(-1,1)`
        );
      else spriteSheet.removeAttributeNS(null, "transform");
    }
  }
}

export class PixelCat extends SpriteCat {
  constructor() {
    super({
      scale: 1.75,
      spriteWidth: 32,
      spriteHeight: 32,
      width: 1024,
      height: 544,
      frameOffset: 0,
      numSheets: 1,
      numCols: 6,
      numFrames: 6,
    });
  }

  async demo(px, py) {
    for (const dir in DIRECTION) {
      for (let frame = 0; frame < 6; frame++) {
        this.display(px, py, DIRECTION[dir], frame);
        await sleep(100);
      }
    }
    this.demo(px, py);
  }

  #getSpriteSheetLocation(dir, frame) {
    let spriteRowNum;
    if (dir === DIRECTION.RIGHT) {
      spriteRowNum = 6;
    } else if (dir === DIRECTION.BOTTOM_RIGHT) {
      spriteRowNum = 7;
      // spriteRowNum = 0;
    } else if (dir === DIRECTION.BOTTOM_LEFT) {
      spriteRowNum = 1;
      // spriteRowNum = 0;
    } else if (dir === DIRECTION.LEFT) {
      spriteRowNum = 2;
    } else if (dir === DIRECTION.TOP_LEFT) {
      spriteRowNum = 3;
      // spriteRowNum = 4;
    } else if (dir === DIRECTION.TOP_RIGHT) {
      spriteRowNum = 5;
      // spriteRowNum = 4;
    } else throw new Error("Invalid dir");

    let spriteColNum = 4;
    if (frame === 0) {
      spriteColNum = 1;
    }

    return [spriteRowNum, spriteColNum];
  }

  display(px, py, dir = null, frame = 0) {
    let movementFactorX = 0;
    let movementFactorY = 0;
    if (dir !== null) {
      if (dir === DIRECTION.LEFT) movementFactorX = -1;
      else if (dir === DIRECTION.RIGHT) movementFactorX = 1;
      else if (dir === DIRECTION.BOTTOM_RIGHT) {
        movementFactorX = 0.68;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.BOTTOM_LEFT) {
        movementFactorX = -0.68;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.TOP_RIGHT) {
        movementFactorX = 0.5;
        movementFactorY = -1;
      } else if (dir === DIRECTION.TOP_LEFT) {
        movementFactorX = -0.5;
        movementFactorY = -1;
      }
    }
    this.shape.setAttributeNS(
      null,
      "transform",
      `translate(${
        px - (32 * this.scale) / 2 + 12 + movementFactorX * frame * 6
      },${py - (30 * this.scale) / 2 + 18 + movementFactorY * frame * 6})`
    );
    if (dir === null) {
      this.shape.removeAttributeNS(XLINKS, "xlink:href");
    } else {
      this.shape.setAttributeNS(XLINKS, "xlink:href", `#${this.spriteId}`);

      this.spriteSheets[0].setAttributeNS(
        null,
        "href",
        "img/skins/pixel/calico_0.png"
      );
      const numSprites = 5;
      const columns = 4; // Number of columns in a bundle
      const rows = 2; // Number of rows in a bundle
      const frameWidth = this.spriteWidth * this.scale; // Adjust based on your sprite sheet
      const frameHeight = this.spriteHeight * this.scale; // Adjust based on your sprite sheet
      let bundleRow = 0; // Adjust based on the target bundle
      let bundleColumn = 0; // Adjust based on the target bundle

      const [spriteRowNum, spriteColNum] = this.#getSpriteSheetLocation(
        dir,
        frame
      );

      let xOffset = frameWidth * 4 * spriteColNum;

      let yOffset = frameHeight + frameHeight * 2 * spriteRowNum;
      if (frame) {
        let offset = 2;
        if ([DIRECTION.TOP_LEFT, DIRECTION.TOP_RIGHT].includes(dir)) offset = 1;
        frame = (frame + offset) % numSprites;
      } else {
        frame = 2;
      }
      let frameX = frame % columns;
      let frameY = Math.floor(frame / columns);
      let xPosition =
        frameX * frameWidth + bundleColumn * frameWidth * columns + xOffset;
      let yPosition =
        frameY * frameHeight + bundleRow * frameHeight * rows + yOffset;

      this.setSpriteSheetLocation(xPosition, yPosition);
    }
  }
}

export class CrossCode extends SpriteCat {
  constructor(
    type = "cat",
    options = {
      scale: 1.85,
      spriteWidth: 24,
      spriteHeight: 32,
      width: 144,
      height: 96,
      frameOffset: 5,
      numFrames: 6,
      numCols: 6,
    }
  ) {
    super(options);
    this.setType(type);
  }

  setType(type) {
    this.spriteSheets[0].setAttributeNS(
      null,
      "href",
      `img/skins/crosscode/${type}.png`
    );
  }

  async demo(px, py) {
    for (const dir in DIRECTION) {
      for (let frame = 0; frame < 6; frame++) {
        this.display(px, py, DIRECTION[dir], frame);
        await sleep(150);
      }
    }
    this.demo(px, py);
  }

  #getSpriteSheetLocation(dir) {
    let spriteRowNum;
    let flipX = false;
    if (dir === DIRECTION.RIGHT) {
      spriteRowNum = 1;
    } else if (dir === DIRECTION.BOTTOM_RIGHT) {
      spriteRowNum = 2;
    } else if (dir === DIRECTION.BOTTOM_LEFT) {
      spriteRowNum = 2;
      flipX = true;
    } else if (dir === DIRECTION.LEFT) {
      spriteRowNum = 1;
      flipX = true;
    } else if (dir === DIRECTION.TOP_LEFT) {
      spriteRowNum = 0;
      flipX = true;
    } else if (dir === DIRECTION.TOP_RIGHT) {
      spriteRowNum = 0;
    } else throw new Error("Invalid dir");

    return [spriteRowNum, flipX];
  }

  setTranslate(px, py, dir, frame) {
    let movementFactorX = 0;
    let movementFactorY = 0;
    if (dir !== null) {
      if (dir === DIRECTION.LEFT) movementFactorX = -1.1;
      else if (dir === DIRECTION.RIGHT) movementFactorX = 1.1;
      else if (dir === DIRECTION.BOTTOM_RIGHT) {
        movementFactorX = 0.5;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.BOTTOM_LEFT) {
        movementFactorX = -0.5;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.TOP_RIGHT) {
        movementFactorX = 0.5;
        movementFactorY = -0.9;
      } else if (dir === DIRECTION.TOP_LEFT) {
        movementFactorX = -0.5;
        movementFactorY = -0.9;
      }
    }
    this.shape.setAttributeNS(
      null,
      "transform",
      `translate(${
        px - (32 * this.scale) / 2 + 19 + movementFactorX * frame * 5
      },${py - (30 * this.scale) / 2 + 6 + movementFactorY * frame * 5})`
    );
  }

  display(px, py, dir = null, frame = 0) {
    this.setTranslate(px, py, dir, frame);
    if (dir === null) {
      this.shape.removeAttributeNS(XLINKS, "xlink:href");
    } else {
      this.shape.setAttributeNS(XLINKS, "xlink:href", `#${this.spriteId}`);
      //   const numSprites = 12;
      const frameWidth = this.spriteWidth * this.scale; // Adjust based on your sprite sheet
      const frameHeight = this.spriteHeight * this.scale; // Adjust based on your sprite sheet

      const [spriteRowNum, flipX] = this.#getSpriteSheetLocation(dir, frame);
      const xPosition =
        ((frame + this.frameOffset) % this.numCols) * frameWidth;
      const yPosition = frameHeight * spriteRowNum;

      this.setSpriteSheetLocation(xPosition, yPosition, flipX, frameWidth);
    }
  }
}

export class CrossCode2 extends CrossCode {
  constructor(
    type = "dino",
    options = {
      scale: 1.7,
      spriteWidth: 40,
      spriteHeight: 32,
      width: 240,
      height: 96,
    }
  ) {
    super(type, options);
  }

  setTranslate(px, py, dir, frame) {
    let movementFactorX = 0;
    let movementFactorY = 0;
    if (dir !== null) {
      if (dir === DIRECTION.LEFT) movementFactorX = -1.4;
      else if (dir === DIRECTION.RIGHT) movementFactorX = 1.4;
      else if (dir === DIRECTION.BOTTOM_RIGHT) {
        movementFactorX = 0.8;
        movementFactorY = 1.1;
      } else if (dir === DIRECTION.BOTTOM_LEFT) {
        movementFactorX = -0.8;
        movementFactorY = 1.1;
      } else if (dir === DIRECTION.TOP_RIGHT) {
        movementFactorX = 0.6;
        movementFactorY = -1.1;
      } else if (dir === DIRECTION.TOP_LEFT) {
        movementFactorX = -0.6;
        movementFactorY = -1.1;
      }
    }
    this.shape.setAttributeNS(
      null,
      "transform",
      `translate(${
        px - (32 * this.scale) / 2 + 6 + ((movementFactorX * frame * 5) / 7) * 6
      },${
        py -
        (30 * this.scale) / 2 +
        12 +
        ((movementFactorY * frame * 5) / 7) * 6
      })`
    );
  }
}

export class CrossCode3 extends CrossCode2 {
  constructor(
    type = "dog",
    options = {
      scale: 1.5,
      spriteWidth: 40,
      spriteHeight: 40,
      width: 240,
      height: 120,
    }
  ) {
    super(type, options);
  }

  setTranslate(px, py, dir, frame) {
    let movementFactorX = 0;
    let movementFactorY = 0;
    if (dir !== null) {
      if (dir === DIRECTION.LEFT) movementFactorX = -1.3;
      else if (dir === DIRECTION.RIGHT) movementFactorX = 1.3;
      else if (dir === DIRECTION.BOTTOM_RIGHT) {
        movementFactorX = 0.6;
        movementFactorY = 1.1;
      } else if (dir === DIRECTION.BOTTOM_LEFT) {
        movementFactorX = -0.6;
        movementFactorY = 1.1;
      } else if (dir === DIRECTION.TOP_RIGHT) {
        movementFactorX = 0.6;
        movementFactorY = -0.9;
      } else if (dir === DIRECTION.TOP_LEFT) {
        movementFactorX = -0.6;
        movementFactorY = -0.9;
      }
    }
    this.shape.setAttributeNS(
      null,
      "transform",
      `translate(${
        px - (32 * this.scale) / 2 + 6 + ((movementFactorX * frame * 5) / 7) * 6
      },${
        py - (30 * this.scale) / 2 + ((movementFactorY * frame * 5) / 7) * 6
      })`
    );
  }
}

export class CrossCode4 extends CrossCode {
  constructor(
    type = "pinguin",
    options = {
      scale: 1.75,
      spriteWidth: 24,
      spriteHeight: 32,
      width: 96,
      height: 96,
      numCols: 4,
      numFrames: 8,
    }
  ) {
    super(type, options);
  }

  setTranslate(px, py, dir, frame) {
    let movementFactorX = 0;
    let movementFactorY = 0;
    if (dir !== null) {
      if (dir === DIRECTION.LEFT) movementFactorX = -1.1;
      else if (dir === DIRECTION.RIGHT) movementFactorX = 1.1;
      else if (dir === DIRECTION.BOTTOM_RIGHT) {
        movementFactorX = 0.5;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.BOTTOM_LEFT) {
        movementFactorX = -0.5;
        movementFactorY = 0.9;
      } else if (dir === DIRECTION.TOP_RIGHT) {
        movementFactorX = 0.5;
        movementFactorY = -0.9;
      } else if (dir === DIRECTION.TOP_LEFT) {
        movementFactorX = -0.5;
        movementFactorY = -0.9;
      }
    }
    this.shape.setAttributeNS(
      null,
      "transform",
      `translate(${
        px -
        (32 * this.scale) / 2 +
        20 +
        ((movementFactorX * frame * 5) / 8) * 6
      },${
        py - (30 * this.scale) / 2 + 8 + ((movementFactorY * frame * 5) / 8) * 6
      })`
    );
  }
}

export function setSteps() {
  stepsEl.innerHTML =
    levelDef.stepsLimit == null ? store.step : levelDef.stepsLimit - store.step;
}

function canCatEscape(cells, cat) {
  const grid = deepCopyGrid(cells);
  const numRows = grid.length;
  const numCols = grid[0].length;

  // Function to check if a cell is valid to move into (not BLOCKED)
  function isValid(i, j) {
    return (
      i >= 0 &&
      i < numRows &&
      j >= 0 &&
      j < numCols &&
      grid[i][j].state !== CELL_STATE.BLOCKED
    );
  }

  // DFS to explore the grid
  function dfs(i, j) {
    // If the cat reaches the edge, it can escape
    if (i === 0 || i === numRows - 1 || j === 0 || j === numCols - 1) {
      return true;
    }

    // Mark the current cell as visited to prevent revisiting
    // We can introduce a new state or use an existing state that signifies visited
    // For example, using CELL_STATE.VISITED if it exists, or CELL_STATE.BLOCKED if not
    grid[i][j].state = CELL_STATE.BLOCKED; // or CELL_STATE.VISITED

    // Determine the neighbors based on row parity
    const addX = i % 2 === 0 ? ADD_X_0 : ADD_X_1;
    const addY = ADD_Y_0; // ADD_Y_0 is used for both even and odd rows

    for (let k = 0; k < 6; k++) {
      const ni = i + addY[k],
        nj = j + addX[k];
      if (isValid(ni, nj) && dfs(ni, nj)) {
        return true;
      }
    }

    // Optional: Reset the cell's state if you are using a special VISITED state
    // grid[i][j].state = originalState;

    return false;
  }

  // Start DFS from the cat's position
  return dfs(cat.y, cat.x);
}

function deepCopyGrid(grid) {
  const copiedGrid = [];
  for (let i = 0; i < grid.length; i++) {
    copiedGrid[i] = [];
    for (let j = 0; j < grid[i].length; j++) {
      // Assuming each cell in the grid is an object
      copiedGrid[i][j] = { ...grid[i][j] };
    }
  }
  return copiedGrid;
}
